import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { apiGet, apiPost } from '../../services/api';
import AdminInvoiceRefundModal from './AdminInvoiceRefundModal';

const MESSAGE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'job_rejected', label: 'Job rejected' },
  { value: 'job_edit_requested', label: 'Job edit requested' },
  { value: 'profile_edit_requested', label: 'Profile edit requested' },
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function boolBadge(value, trueLabel, falseLabel) {
  return (
    <Badge bg={value ? 'success' : 'secondary'} className="rwm-admin__badge me-1">
      {value ? trueLabel : falseLabel}
    </Badge>
  );
}

function displayOrDash(value) {
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

export default function AdminCompanyDetailModal({
  company,
  token,
  onClose,
  onAction,
  onGiftCredits,
  handleAuthError,
  setError,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [msgType, setMsgType] = useState('general');
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState(false);
  const [msgError, setMsgError] = useState('');
  const [refundInvoice, setRefundInvoice] = useState(null);
  const [featuringJobId, setFeaturingJobId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadDetail = useCallback(async () => {
    if (!token || !company?.id) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiGet(`/api/v1/admin/companies/${company.id}/detail`, token);
      if (handleAuthError(res)) return;
      if (res.status === 404) {
        setFetchError('Company not found.');
        return;
      }
      if (!res.ok) {
        setFetchError('Could not load company details.');
        return;
      }
      const json = await res.json();
      setDetail(json.data ?? null);
    } catch {
      setFetchError('Could not load company details.');
    } finally {
      setLoading(false);
    }
  }, [company?.id, token, handleAuthError]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const runAction = async (action) => {
    if (!company?.id) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await apiPost(`/api/v1/admin/companies/${company.id}/${action}`, {}, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError(`Could not ${action} company.`);
        return;
      }
      setConfirmAction(null);
      onAction();
    } catch {
      setError(`Could not ${action} company.`);
    } finally {
      setActionLoading(false);
    }
  };

  const closeConfirm = () => setConfirmAction(null);

  const toggleJobFeatured = async (jobId) => {
    if (!jobId) return;
    setFeaturingJobId(jobId);
    setError('');
    try {
      const res = await apiPost(`/api/v1/admin/jobs/${jobId}/feature`, {}, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not update featured status.');
        return;
      }
      const json = await res.json();
      const isFeatured = Boolean(json.data?.is_featured);
      setDetail((prev) => {
        if (!prev || !Array.isArray(prev.jobs)) return prev;
        return {
          ...prev,
          jobs: prev.jobs.map((job) =>
            job.id === jobId ? { ...job, is_featured: isFeatured } : job
          ),
        };
      });
    } catch {
      setError('Could not update featured status.');
    } finally {
      setFeaturingJobId(null);
    }
  };

  const sendMessage = async () => {
    if (!company?.id || !msgSubject.trim() || !msgBody.trim()) return;
    setMsgSending(true);
    setMsgError('');
    setMsgSuccess(false);
    try {
      const res = await apiPost(
        `/api/v1/admin/companies/${company.id}/messages`,
        {
          type: msgType,
          subject: msgSubject.trim(),
          body: msgBody.trim(),
        },
        token
      );
      if (handleAuthError(res)) return;
      if (res.status === 422) {
        setMsgError('Please check the message fields and try again.');
        return;
      }
      if (!res.ok) {
        setMsgError('Could not send message.');
        return;
      }
      setMsgSuccess(true);
      setMsgSubject('');
      setMsgBody('');
      setMsgType('general');
    } catch {
      setMsgError('Could not send message.');
    } finally {
      setMsgSending(false);
    }
  };

  const showApprove = detail && !detail.is_approved;
  const isBanned = Boolean(detail?.is_banned);
  const isSuspended = detail && !detail.is_active && !isBanned;
  const showSuspend = detail && detail.is_active && !isBanned;
  const showBan = detail && detail.is_active && !isBanned;
  const companyName = detail?.name || company?.name || 'this company';

  return (
    <Modal
      show
      onHide={onClose}
      size="lg"
      centered
      data-testid="rwm-admin-company-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Company details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : null}
        {!loading && fetchError ? (
          <Alert variant="danger" className="mb-0">
            {fetchError}
          </Alert>
        ) : null}
        {!loading && detail ? (
          <>
            <section className="mb-4">
              <h5 className="mb-1" data-testid="rwm-admin-company-modal-name">
                {detail.name}
              </h5>
              {detail.legal_name ? (
                <p className="text-muted mb-2">{detail.legal_name}</p>
              ) : null}
              {detail.website ? (
                <p className="mb-2" data-testid="rwm-admin-company-modal-website">
                  <a href={detail.website} target="_blank" rel="noopener noreferrer">
                    {detail.website}
                  </a>
                </p>
              ) : (
                <p className="mb-2 text-muted" data-testid="rwm-admin-company-modal-website">
                  —
                </p>
              )}
              <p className="mb-1">
                <strong>Country:</strong> {displayOrDash(detail.country)}
                {' · '}
                <strong>City:</strong> {displayOrDash(detail.city)}
              </p>
              <p className="mb-1">
                <strong>Address:</strong> {displayOrDash(detail.address)}
              </p>
              <div data-testid="rwm-admin-company-modal-description">
                <h6 className="mt-3">Description</h6>
                <p className="mb-0">{displayOrDash(detail.description)}</p>
              </div>
              {detail.employee_count ? (
                <p className="mb-0 mt-2">
                  <strong>Employees:</strong> {detail.employee_count}
                </p>
              ) : null}
              {detail.founded_year ? (
                <p className="mb-0">
                  <strong>Founded:</strong> {detail.founded_year}
                </p>
              ) : null}
            </section>

            <section className="mb-4" data-testid="rwm-admin-company-modal-vat">
              <h6>VAT &amp; Billing</h6>
              <p className="mb-1">
                <strong>VAT ID:</strong>{' '}
                {detail.vat_id ? detail.vat_id : 'Not provided'}
              </p>
              <p className="mb-2">
                <strong>VAT Country:</strong>{' '}
                {detail.vat_country ? detail.vat_country : 'Not provided'}
              </p>
              {boolBadge(detail.is_approved, 'Approved', 'Not approved')}
              {boolBadge(detail.is_active, 'Active', 'Inactive')}
              {isBanned ? (
                <Badge bg="danger" className="rwm-admin__badge me-1">
                  Banned
                </Badge>
              ) : null}
            </section>

            <section className="mb-4" data-testid="rwm-admin-company-modal-billing">
              <h6>Billing &amp; Invoices</h6>
              {Array.isArray(detail.invoices) && detail.invoices.length > 0 ? (
                <div className="list-group list-group-flush">
                  {detail.invoices.map((invoice) => {
                    const refunded = Number(invoice.refunded_amount) || 0;
                    const total = Number(invoice.total) || 0;
                    const canRefund =
                      invoice.has_stripe_session && !invoice.is_fully_refunded;

                    return (
                      <div
                        key={invoice.id}
                        className="list-group-item px-0"
                        data-testid={`rwm-admin-invoice-row-${invoice.id}`}
                      >
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                          <div>
                            <div className="fw-medium">{invoice.invoice_number}</div>
                            <div className="small text-muted">
                              {formatDate(invoice.issued_at)}
                              {invoice.package_type ? ` · ${invoice.package_type}` : ''}
                            </div>
                            <div className="small mt-1">
                              Total: €{total.toFixed(2)}
                            </div>
                            {refunded > 0 ? (
                              <div
                                className="small mt-1"
                                style={{ color: 'var(--rwm-amber)' }}
                              >
                                Refunded: €{refunded.toFixed(2)}
                              </div>
                            ) : null}
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            {invoice.is_fully_refunded ? (
                              <Badge bg="secondary">Fully Refunded</Badge>
                            ) : null}
                            {canRefund ? (
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                onClick={() => setRefundInvoice(invoice)}
                                data-testid={`rwm-invoice-refund-btn-${invoice.id}`}
                              >
                                Refund
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted small mb-0">No invoices yet.</p>
              )}
            </section>

            <section className="mb-4" data-testid="rwm-admin-company-modal-activity">
              <h6>Activity</h6>
              <p className="mb-1">Total jobs submitted: {detail.jobs_total}</p>
              <p className="mb-1">Approved jobs: {detail.jobs_approved}</p>
              <p className="mb-1">Pending jobs: {detail.jobs_pending}</p>
              <p className="mb-0 text-muted">Member since: {formatDate(detail.created_at)}</p>
            </section>

            {Array.isArray(detail.jobs) && detail.jobs.length > 0 ? (
              <section className="mb-4" data-testid="rwm-admin-company-modal-jobs">
                <h6>Approved Jobs</h6>
                <div className="list-group list-group-flush">
                  {detail.jobs.map((job) => (
                    <div key={job.id} className="list-group-item px-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div>
                        <span className="fw-medium">{job.title}</span>
                        {job.is_featured ? (
                          <Badge
                            bg="warning"
                            text="dark"
                            className="ms-2"
                            data-testid={`rwm-admin-job-featured-badge-${job.id}`}
                          >
                            ★ Featured
                          </Badge>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant={job.is_featured ? 'outline-secondary' : 'warning'}
                        size="sm"
                        disabled={featuringJobId === job.id}
                        onClick={() => toggleJobFeatured(job.id)}
                        data-testid={`rwm-admin-job-feature-${job.id}`}
                      >
                        {featuringJobId === job.id
                          ? 'Saving…'
                          : job.is_featured
                            ? '☆ Remove Featured'
                            : '★ Mark as Featured'}
                      </Button>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section data-testid="rwm-admin-company-modal-send-message">
              <h6>Send Message</h6>
              {msgSuccess ? (
                <Alert variant="success" className="py-2">
                  Message sent
                </Alert>
              ) : null}
              {msgError ? (
                <Alert variant="danger" className="py-2">
                  {msgError}
                </Alert>
              ) : null}
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Type</Form.Label>
                <Form.Select
                  value={msgType}
                  onChange={(e) => setMsgType(e.target.value)}
                  data-testid="rwm-admin-msg-type"
                >
                  {MESSAGE_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Subject</Form.Label>
                <Form.Control
                  value={msgSubject}
                  onChange={(e) => setMsgSubject(e.target.value)}
                  maxLength={255}
                  data-testid="rwm-admin-msg-subject"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-1">Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  maxLength={2000}
                  data-testid="rwm-admin-msg-body"
                />
              </Form.Group>
              <Button
                type="button"
                variant="info"
                className="text-white"
                disabled={msgSending || !msgSubject.trim() || !msgBody.trim()}
                onClick={sendMessage}
                data-testid="rwm-admin-msg-send"
              >
                {msgSending ? 'Sending…' : 'Send Message'}
              </Button>
            </section>
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer className="d-flex flex-wrap">
        <Button
          variant="outline-secondary"
          onClick={onClose}
          data-testid="rwm-admin-company-modal-back"
        >
          Back to list
        </Button>
        <div className="flex-grow-1" />
        {detail && !loading && !fetchError ? (
          <>
            <Button
              variant="outline-info"
              className="me-2"
              onClick={onGiftCredits}
              data-testid="rwm-admin-company-modal-gift"
            >
              Gift Credits
            </Button>
            {showApprove ? (
              <Button
                variant="info"
                className="me-2"
                disabled={actionLoading}
                onClick={() => runAction('approve')}
                data-testid="rwm-admin-company-modal-approve"
              >
                Approve
              </Button>
            ) : null}
            {showSuspend ? (
              <Button
                variant="outline-warning"
                className="me-2"
                disabled={actionLoading}
                onClick={() => setConfirmAction('suspend')}
                data-testid="rwm-admin-company-modal-suspend"
              >
                Suspend
              </Button>
            ) : null}
            {isSuspended ? (
              <Button
                variant="outline-success"
                className="me-2"
                disabled={actionLoading}
                onClick={() => setConfirmAction('unsuspend')}
                data-testid="rwm-admin-company-modal-unsuspend"
              >
                Unsuspend
              </Button>
            ) : null}
            {showBan ? (
              <Button
                variant="outline-danger"
                disabled={actionLoading}
                onClick={() => setConfirmAction('ban')}
                data-testid="rwm-admin-company-modal-ban"
              >
                Ban
              </Button>
            ) : null}
            {isBanned ? (
              <Badge
                bg="danger"
                className="rwm-admin__badge align-self-center"
                data-testid={`rwm-admin-company-banned-badge-${company.id}`}
              >
                Banned
              </Badge>
            ) : null}
          </>
        ) : null}
      </Modal.Footer>

      {confirmAction === 'suspend' ? (
        <Modal
          show
          onHide={closeConfirm}
          centered
          data-testid="rwm-admin-suspend-confirm-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Suspend Company</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            This will immediately block {companyName} from accessing the platform and archive
            their active listings. You can unsuspend them later.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={closeConfirm}
              disabled={actionLoading}
              data-testid="rwm-admin-suspend-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={() => runAction('suspend')}
              disabled={actionLoading}
              data-testid="rwm-admin-suspend-confirm-btn"
            >
              {actionLoading ? 'Suspending…' : 'Suspend'}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}

      {confirmAction === 'ban' ? (
        <Modal show onHide={closeConfirm} centered data-testid="rwm-admin-ban-confirm-modal">
          <Modal.Header closeButton>
            <Modal.Title>Ban Company</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            This will permanently ban {companyName} from RemoteWorkMarket. This action cannot be
            undone.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={closeConfirm}
              disabled={actionLoading}
              data-testid="rwm-admin-ban-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => runAction('ban')}
              disabled={actionLoading}
              data-testid="rwm-admin-ban-confirm-btn"
            >
              {actionLoading ? 'Banning…' : 'Ban Permanently'}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}

      {confirmAction === 'unsuspend' ? (
        <Modal
          show
          onHide={closeConfirm}
          centered
          data-testid="rwm-admin-unsuspend-confirm-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Unsuspend Company</Modal.Title>
          </Modal.Header>
          <Modal.Body>This will restore {companyName}&apos;s access to the platform.</Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={closeConfirm}
              disabled={actionLoading}
              data-testid="rwm-admin-unsuspend-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => runAction('unsuspend')}
              disabled={actionLoading}
              data-testid="rwm-admin-unsuspend-confirm-btn"
            >
              {actionLoading ? 'Unsuspending…' : 'Unsuspend'}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}

      {refundInvoice ? (
        <AdminInvoiceRefundModal
          invoiceId={refundInvoice.id}
          invoiceTotal={refundInvoice.total}
          refundedAmount={refundInvoice.refunded_amount}
          token={token}
          onRefunded={() => {
            setRefundInvoice(null);
            loadDetail();
          }}
          onClose={() => setRefundInvoice(null)}
        />
      ) : null}
    </Modal>
  );
}
